import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RoomTitleProps, RouteParamsWithLanguage } from "../../../../../../types/screensTypes";
import Button from "../../../../../../components/ui/Button";
import { saveSubPlan, getSubplanbyId } from "../../../../../../api/museum";
import NumberInput from "../../../../../../components/common/NumberInput";
import TextInput from "../../../../../../components/ui/TextInput";
import { useTrackCreation } from "../../../../../../context/TrackCreationContext";
import { idToLanguage } from "../../../../../../utils/idtolanguage";
import { useGlobalState } from "../../../../../../context/useGlobalState";
import floorEdit from "./FloorEdit.module.css";
import stepStyles from "../../../createStep.module.css";

const RoomTitle: React.FC<RoomTitleProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { roomId, language } = useParams<RouteParamsWithLanguage>(); // Get room ID and language from the URL
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();

    const [roomName, setRoomName] = useState<string>("");
    const [floorNumber, setFloorNumber] = useState<number>(0);

    const saveRoom = async (): Promise<void> => {
        const token = localStorage.getItem("token"); // Retrieve token
        const trackId = trackData.trackId; // Retrieve track ID

        if (!token || !trackId || !language || !roomId || floorNumber == null || !roomName) return;

        const saveRoomInfo = await saveSubPlan({
            token,
            trackId,
            subplanId: roomId,
            language,
            floorNumber,
            name: roomName,
        });
        if (saveRoomInfo && saveRoomInfo.subPlanId) {
            const id = saveRoomInfo.subPlanId;
            navigateToNext(id, language);
        }
    };

    const getRoomInfo = async (trackId: string, roomId: string, language: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !trackId || !roomId || !language) return;
        const subplanInfo = await getSubplanbyId({
            token,
            trackId,
            subplanId: roomId,
            language,
        });
        const localizedInfo = subplanInfo?.subPlansLocalized;
        const floorNum = subplanInfo?.floorNumber;
        setFloorNumber(typeof floorNum === "number" ? floorNum : 0);
        if (localizedInfo && localizedInfo.length > 0) {
            const localized = localizedInfo[0];
            const name = localized.name || localized.roomName || subplanInfo.internalName || "";
            setRoomName(name);
        } else if (subplanInfo?.internalName) {
            setRoomName(subplanInfo.internalName);
        }
    };

    useEffect(() => {
        if (roomId && roomId !== "" && roomId !== "null" && trackData.trackId && language) {
            getRoomInfo(trackData.trackId, roomId, language);
        }
    }, [roomId, trackData.trackId, language]);

    return (
        <div className={`experience-floor-edit ${floorEdit.root}`}>
            <h3 className={`experience-floor-h ${floorEdit.heading}`}>
                {t("name")} {idToLanguage(language || "", data?.languagesAI, t, { titleCase: true })}
            </h3>
            {floorNumber !== null && (
                <TextInput
                    placeholder={t("title")}
                    value={roomName}
                    size="small"
                    onChange={(e) => setRoomName(e.target.value)}
                />
            )}

            <h3 className={`experience-floor-h experience-floor-h--gap ${floorEdit.heading} ${floorEdit.headingGap}`}>
                {t("floorNumber")}
            </h3>
            <NumberInput
                placeholder={t("selectTheFloorNumber")}
                value={floorNumber !== null ? floorNumber : 0}
                size="medium"
                onChange={(e) => {
                    const value = e.target.value;
                    setFloorNumber(value ? Number(value) : 0);
                }}
            />

            <div className={`${stepStyles.stepButton} ${floorEdit.actions}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveRoom}
                />
            </div>
        </div>
    );
};

export default RoomTitle;
